// TODO: Fix linting error https://jira.24i.com/browse/PRJ1260EXT-4659
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { View, Text, BackHandler } from 'react-native';
import PropTypes from 'prop-types';
import WebCookieInfo from 'LocalGluons/components/WebCookieInfo';
import { logEvent } from 'AppManager';
import { addNetInfoListener, Device } from 'DeviceUtils';
import FadeView from 'LocalGluons/components/FadeView';
import { NotificationsContextProvider } from 'LocalGluons/components/NotificationsContext/NotificationsContext';
import { LanguageSettingsContextProvider } from 'LocalGluons/components/LanguageSettingsContext/LanguageSettingsContext';
import { WebSearchContextProvider } from 'Pages/Search/WebSearchContext';
import { FavoriteSettingsProvider } from 'Pages/FavoriteSettings/FavoriteSettingsContext';
import { SettingsNavigationContextProvider } from 'Pages/Settings/components/SettingsNavigationContext/SettingsNavigationContext';
import { Picker } from 'LocalGluons';
import InitialScreen from 'Pages/AppEntry/components/InitialScreen';
import { useNavigation } from '@react-navigation/native';
import SimpleNotificationProvider from 'Pages/SimpleNotification/SimpleNotificationProvider';
import { useAppEntryContext } from 'Pages/AppEntry';
import { REVERSED_DETAIL_TYPE } from 'Options';
import InitialErrorMessage from 'Pages/AppEntry/components/InitialErrorMessage';
import { prepareMenuItems } from './navigationUtils';
import AppModal from '../App/AppModal';
import AppContainer from './appContainerFactory';
import AppPlayer from '../App/AppPlayer';
import NavigationService from './navigationService';
import WebUserAgentCheck from './webUserAgentCheck';
import AppPromoScreen from '../Page/AppPromo';
import { AppErrorHandlerProvider } from '../App/AppErrorHandlerContext/AppErrorHandlerContext';
import { SystemInfoHelper } from '../../AppManager/helpers/SystemInfoHelper';
import { ChromecastContextProvider } from 'LocalGluons/components/ChromecastContext';
import { useNavigationPropsContext } from './navigationPropsContext';

const AppStack = (props) => {
    const { insertedPath, pathOptions } = props;
    const { initializedData, errorMessage, initialize } = useAppEntryContext();
    const { navigate } = useNavigationPropsContext();
    const connectionLostEvent = useRef(null);
    const { navigationChangeHandler } = NavigationService;
    const loggedIn = initializedData && initializedData.userData !== null;
    const [from, setFrom] = useState(undefined);
    const [to, setTo] = useState(undefined);

    const navigation = useNavigation();

    NavigationService.setTopLevelNavigationInstance(navigation);

    const [insertedPathObject, setInsertedPathObject] = useState({
        mainLocation: loggedIn ? 'home' : '',
        secondLayer: '',
        asset: {
            type: '',
            id: null,
            platform: ''
        }
    });

    const decomposeInsertedPath = (pathToDecompose = '') => {
        if (pathToDecompose === '' || pathToDecompose === null) return;

        const pathArray = pathToDecompose.split(/[/|?|&]/);

        if (pathArray[1] !== 'home' && !loggedIn) {
            navigate('home');
        }
        let assetProvider;
        let detailId;
        switch (pathArray.length) {
            case 2:
                setInsertedPathObject({
                    mainLocation: pathArray[1],
                    secondLayer: null,
                    asset: null
                });
                break;
            case 3:
                setInsertedPathObject({
                    mainLocation: pathArray[1],
                    // check if it contains only numbers ->if does then it is detail ID if not it is path reference
                    secondLayer: /^[\d]+$/.test(pathArray[2]) ? null : pathArray[2],
                    asset: null
                });
                break;
            case 4:
                assetProvider = pathArray[pathArray.length - 1].split('=')[1];
                detailId = pathArray[pathArray.length - 1];
                setInsertedPathObject({
                    mainLocation: pathArray[1],
                    secondLayer: null,
                    asset: {
                        type: REVERSED_DETAIL_TYPE[pathArray[2]],
                        id: /^[\d]+$/.test(detailId) ? detailId : null,
                        platform: assetProvider ?? null
                    }
                });
                break;
            case 5:
                assetProvider = pathArray[pathArray.length - 1].split('=')[1];
                detailId = pathArray[pathArray.length - 2];
                setInsertedPathObject({
                    mainLocation: pathArray[1],
                    secondLayer: null,
                    asset: {
                        type: REVERSED_DETAIL_TYPE[pathArray[2]],
                        id: /^[\d]+$/.test(detailId) ? detailId : null,
                        platform: assetProvider
                    }
                });
                break;
            case 6:
                assetProvider = pathArray[pathArray.length - 1].split('=')[1];
                const parentId = pathArray[pathArray.length - 2].split('=')[1];
                detailId = pathArray[pathArray.length - 3];
                setInsertedPathObject({
                    mainLocation: pathArray[1],
                    secondLayer: null,
                    asset: {
                        type: REVERSED_DETAIL_TYPE[pathArray[2]],
                        id: /^[\d]+$/.test(detailId) ? detailId : null,
                        parentId,
                        platform: assetProvider
                    }
                });
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        decomposeInsertedPath(insertedPath);
    }, [insertedPath]);

    useEffect(() => {
        const unsubscribe = addNetInfoListener(async ({ isInternetReachable }) => {
            if (!isInternetReachable) {
                const currentRoute = NavigationService.getCurrentRoute();
                const systemInfo = new SystemInfoHelper();
                await systemInfo.load();
                connectionLostEvent.current = {
                    time: Date.now(),
                    type: 'error',
                    subtype: 'connectivity',
                    error: 'connectionLost',
                    context: currentRoute,
                    ...systemInfo.get()
                };
            } else if (connectionLostEvent.current) {
                // After connection is back, send event about connection was lost
                logEvent(connectionLostEvent.current);
                connectionLostEvent.current = null;
            }
        });
        if (typeof onLoadCallback === 'function') onLoadCallback();

        return () => {
            unsubscribe();
        };
    }, []);

    const menuItems = useMemo(() => {
        if (initializedData?.menus) {
            return prepareMenuItems(initializedData?.menus);
        }
        return {
            items: [],
            routes: {}
        };
    }, [initializedData]);

    if (errorMessage) {
        return <InitialErrorMessage errorMessage={errorMessage} onRetryPress={initialize} />;
    }

    if (initializedData === null) {
        return <InitialScreen showSplash={!Device.isWeb} />;
    }

    const { userData, onboardingCompleted, customOnboardingItems } = initializedData;

    if (WebUserAgentCheck.isMobileOrTablet()) {
        return <AppPromoScreen />;
    }

    if (Device.isWeb) {
        return (
            <WebSearchContextProvider>
                <WebCookieInfo>
                    <AppModal>
                        <AppErrorHandlerProvider logErrorFunction={logEvent}>
                            <NotificationsContextProvider>
                                <LanguageSettingsContextProvider>
                                    <SettingsNavigationContextProvider>
                                        <FavoriteSettingsProvider>
                                            <SimpleNotificationProvider>
                                                <AppContainer
                                                    onNavigationStateChange={navigationChangeHandler(
                                                        logEvent
                                                    )}
                                                    menuItems={menuItems}
                                                    userData={userData}
                                                    insertedPath={insertedPathObject}
                                                    pathOptions={pathOptions}
                                                />
                                            </SimpleNotificationProvider>
                                        </FavoriteSettingsProvider>
                                    </SettingsNavigationContextProvider>
                                    <AppPlayer />
                                </LanguageSettingsContextProvider>
                            </NotificationsContextProvider>
                        </AppErrorHandlerProvider>
                    </AppModal>
                </WebCookieInfo>
            </WebSearchContextProvider>
        );
    }

    return (
        <FadeView>
            <AppModal>
                <AppErrorHandlerProvider logErrorFunction={logEvent}>
                    <NotificationsContextProvider>
                        <LanguageSettingsContextProvider>
                            <ChromecastContextProvider>
                                <SettingsNavigationContextProvider>
                                    <FavoriteSettingsProvider>
                                        <SimpleNotificationProvider>
                                            <AppContainer
                                                onNavigationStateChange={navigationChangeHandler(
                                                    logEvent
                                                )}
                                                menuItems={menuItems}
                                                userData={userData}
                                                onboardingCompleted={onboardingCompleted}
                                                showEnforceChangePassword={false}
                                                customOnboardingItems={customOnboardingItems}
                                                navigation={navigation}
                                            />
                                        </SimpleNotificationProvider>
                                        <Picker.DropdownContent />
                                    </FavoriteSettingsProvider>
                                </SettingsNavigationContextProvider>
                                <AppPlayer />
                            </ChromecastContextProvider>
                        </LanguageSettingsContextProvider>
                    </NotificationsContextProvider>
                </AppErrorHandlerProvider>
            </AppModal>
        </FadeView>
    );
};

AppStack.propTypes = {
    // navigation: PropTypes.shape({}).isRequired
};

export default AppStack;
